import { type TopIndependentStatus } from '#app/__generated__/api.js';
import {
  type RatingMenuMutation,
  type RatingMenuMutation$data,
} from '#app/__generated__/relay/RatingMenuMutation.graphql.js';
import { Checkbox } from '#app/components/Controls/src/Checkbox.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { springAnimation } from '#app/utilities/animation.js';
import { CellLayout } from '../CellLayout.js';
import { ButtonGroup } from './ButtonGroup.js';
import {
  RatingCategoryLabel,
  RatingCategoryLayout,
  RatingCategoryValues,
  RatingGrid,
  StyledButton,
  ToggleButton,
} from './RatingMenu.styles.js';
import { TopIndependentStatusSelect } from './TopIndependentStatusSelect.js';
import {
  ActionTypes,
  Categories,
  type RatingMenuProps,
  type RatingsAction,
  type RatingsState,
  type RatingSystem,
} from './types.js';
import { ChevronDownIcon } from '@contra/icons/icons';
// disable useContraMutation eslint error
import { graphql, useMutation } from '@contra/relay';
import { type FormEvent, useCallback, useReducer, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { styled } from 'styled-components';

const Textarea = styled(TextareaAutosize)`
  resize: none;
  display: flex;
`;

const ratingSystem: RatingSystem = [
  {
    category: Categories.BASIC_SCORE,
    label: 'Basics',
    values: [0, 1, 2],
  },
  {
    category: Categories.PROJECTS_SCORE,
    label: 'Projects',
    values: [0, 1, 2, 3],
  },
  {
    category: Categories.SERVICES_SCORE,
    label: 'Services',
    values: [0, 1, 2, 3],
  },
  {
    category: Categories.BONUS_SCORE,
    label: 'Bonus',
    values: [-1, 0, 1, 2],
  },
  {
    category: Categories.TOP_INDEPENDENT_STATUS,
    label: 'Top Independent',
  },
  {
    category: Categories.INTERNAL_NOTES,
    label: 'Internal notes',
  },
  {
    category: Categories.EXCLUDE_FROM_DISCOVER,
    label: 'Exclude from Discover',
  },
  {
    category: Categories.EXCLUDE_FROM_MATCHING,
    label: 'Exclude from matching',
  },
  {
    category: Categories.FLAG_FOR_IMPROVEMENT,
    label: 'Flag for improvement',
  },
];

const calculateTotalRating = (rating: RatingsState['rating']): number => {
  return Object.keys(rating)
    .filter((category) => typeof rating[category] === 'number')
    .reduce((accumulator, category) => {
      // Wrapping in Number to satisfy TS—we've filtered out
      // boolean values, so we can safely use Number here
      return accumulator + Number(rating[category]);
    }, 0);
};

const reducer = (state: RatingsState, action: RatingsAction): RatingsState => {
  switch (action.type) {
    case ActionTypes.HANDLE_EXCLUDE_FROM_DISCOVER_CHANGE:
      return {
        hasChanges: true,
        rating: {
          ...state.rating,
          [action.payload.category]: action.payload.value,
        },
      };
    case ActionTypes.HANDLE_EXCLUDE_FROM_MATCHING_CHANGE:
      return {
        hasChanges: true,
        rating: {
          ...state.rating,
          [action.payload.category]: action.payload.value,
        },
      };
    case ActionTypes.HANDLE_FLAG_FOR_IMPROVEMENT_CHANGE:
      return {
        hasChanges: true,
        rating: {
          ...state.rating,
          [action.payload.category]: action.payload.value,
        },
      };
    case ActionTypes.HANDLE_INTERNAL_NOTES_CHANGE:
      return {
        hasChanges: true,
        rating: {
          ...state.rating,
          [action.payload.category]: action.payload.value,
        },
      };
    case ActionTypes.HANDLE_TOP_INDEPENDENT_STATUS_CHANGE:
      return {
        hasChanges: true,
        rating: {
          ...state.rating,
          [action.payload.category]: action.payload.value,
        },
      };
    default:
      throw new Error('Unexpected action');
  }
};

export const RatingMenu = ({
  currentRating,
  onMutationSuccess,
  userId,
}: RatingMenuProps) => {
  const [state, dispatch] = useReducer(reducer, currentRating);

  const getTotalRating = useCallback((): number => {
    return calculateTotalRating(state.rating);
  }, [state.rating]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const hasPermissions = useHasUserFeatures(['RATE_USERS']);
  const toast = useToast();

  // Note: we no longer rate users manually, but for now we still use the `adminRateUserProfile`
  // mutation to update fields like `internalNotes`.
  const [rateUser, isRatingUser] = useMutation<RatingMenuMutation>(graphql`
    mutation RatingMenuMutation($input: AdminRateUserProfileInput!) {
      adminRateUserProfile(input: $input) {
        adminUserSearchResult {
          id
          userProfile {
            topIndependentStatus
          }
          rating {
            id
            excludeFromDiscover
            excludeFromMatching
            flagForImprovement
            internalNotes
          }
        }
        errors {
          message
        }
      }
    }
  `);

  const toggleMenu = useCallback(() => {
    setIsOpen((currentState) => !currentState);
  }, []);

  const handleTopIndependentStatusChange = useCallback(
    (status: TopIndependentStatus) => {
      dispatch({
        payload: {
          category: Categories.TOP_INDEPENDENT_STATUS,
          value: status,
        },
        type: ActionTypes.HANDLE_TOP_INDEPENDENT_STATUS_CHANGE,
      });
    },
    [],
  );

  const handleInternalNotesChange = useCallback(
    (event: FormEvent<HTMLTextAreaElement>) => {
      dispatch({
        payload: {
          category: Categories.INTERNAL_NOTES,
          value: event.currentTarget.value,
        },
        type: ActionTypes.HANDLE_INTERNAL_NOTES_CHANGE,
      });
    },
    [],
  );

  const handleExcludeFromDiscoverChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      dispatch({
        payload: {
          category: Categories.EXCLUDE_FROM_DISCOVER,
          value: event.currentTarget.checked,
        },
        type: ActionTypes.HANDLE_EXCLUDE_FROM_DISCOVER_CHANGE,
      });
    },
    [],
  );

  const handleExcludeFromMatchingChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      dispatch({
        payload: {
          category: Categories.EXCLUDE_FROM_MATCHING,
          value: event.currentTarget.checked,
        },
        type: ActionTypes.HANDLE_EXCLUDE_FROM_MATCHING_CHANGE,
      });
    },
    [],
  );

  const handleFlagForImprovementChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      dispatch({
        payload: {
          category: Categories.FLAG_FOR_IMPROVEMENT,
          value: event.currentTarget.checked,
        },
        type: ActionTypes.HANDLE_FLAG_FOR_IMPROVEMENT_CHANGE,
      });
    },
    [],
  );

  const handleRatingSave = useCallback(() => {
    if (hasPermissions && state.hasChanges) {
      rateUser({
        onCompleted: ({ adminRateUserProfile }: RatingMenuMutation$data) => {
          if (adminRateUserProfile?.errors?.length) {
            toast.error('There was an issue updating the rating of this user');
          } else {
            toast.success('Successfully updated user rating status');
            onMutationSuccess?.();
            toggleMenu();
          }
        },
        onError: () => {
          toast.error('There was an issue updating the rating of this user');
        },
        variables: {
          input: {
            excludeFromDiscover: state.rating.excludeFromDiscover,
            excludeFromMatching: state.rating.excludeFromMatching,
            flagForImprovement: state.rating.flagForImprovement,
            id: userId,
            internalNotes: state.rating.internalNotes,
            topIndependentStatus: state.rating.topIndependentStatus,
          },
        },
      });
    }
  }, [
    hasPermissions,
    state.hasChanges,
    state.rating.excludeFromDiscover,
    state.rating.excludeFromMatching,
    state.rating.flagForImprovement,
    state.rating.topIndependentStatus,
    state.rating.internalNotes,
    rateUser,
    toast,
    toggleMenu,
    userId,
    onMutationSuccess,
  ]);

  return hasPermissions ? (
    <Menu
      isOpen={isOpen}
      toggleMenu={toggleMenu}
    >
      <Menu.Reference>
        <ToggleButton
          as="button"
          onClick={toggleMenu}
          type="button"
        >
          <CellLayout>{getTotalRating()}</CellLayout>
          <animated.div
            animate={{
              rotate: isOpen ? 180 : 0,
            }}
            initial={false}
            transition={springAnimation.fast}
          >
            <ChevronDownIcon size={24} />
          </animated.div>
        </ToggleButton>
      </Menu.Reference>
      <Menu.Panel>
        <RatingCategoryLayout>
          <RatingGrid>
            {ratingSystem
              .filter((rating) => rating.values)
              .map((rating) => {
                return rating.values ? (
                  <div key={`rating-${rating.label}`}>
                    <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                    <RatingCategoryValues>
                      <ButtonGroup
                        active={rating.values.indexOf(
                          Number(state.rating[rating.category]),
                        )}
                        buttons={rating.values}
                        category={rating.category}
                      />
                    </RatingCategoryValues>
                  </div>
                ) : null;
              })}
          </RatingGrid>
          <RatingGrid>
            {ratingSystem
              .filter((rating) => !rating.values)
              .map((rating) => {
                if (rating.category === 'topIndependentStatus') {
                  return (
                    <div key={`rating-${rating.label}`}>
                      <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                      <TopIndependentStatusSelect
                        onSelection={handleTopIndependentStatusChange}
                        selection={state.rating.topIndependentStatus}
                      />
                    </div>
                  );
                }

                if (rating.category === 'excludeFromDiscover') {
                  return (
                    <div key={`rating-${rating.label}`}>
                      <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                      <Checkbox
                        id="excludeFromDiscover"
                        isToggle
                        name="excludeFromDiscover"
                        onChange={handleExcludeFromDiscoverChange}
                        preChecked={state.rating.excludeFromDiscover ?? false}
                      />
                    </div>
                  );
                }

                if (rating.category === 'excludeFromMatching') {
                  return (
                    <div key={`rating-${rating.label}`}>
                      <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                      <Checkbox
                        id="excludeFromMatching"
                        isToggle
                        name="excludeFromMatching"
                        onChange={handleExcludeFromMatchingChange}
                        preChecked={state.rating.excludeFromMatching ?? false}
                      />
                    </div>
                  );
                }

                if (rating.category === 'flagForImprovement') {
                  return (
                    <div key={`rating-${rating.label}`}>
                      <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                      <Checkbox
                        id="flagForImprovement"
                        isToggle
                        name="flagForImprovement"
                        onChange={handleFlagForImprovementChange}
                        preChecked={state.rating.flagForImprovement ?? false}
                      />
                    </div>
                  );
                }

                if (rating.category === 'internalNotes') {
                  return (
                    <div
                      key={`rating-${rating.label}`}
                      style={{ width: '100%' }}
                    >
                      <RatingCategoryLabel>{rating.label}</RatingCategoryLabel>
                      <Textarea
                        id="internalNotes"
                        minRows={4}
                        name="internalNotes"
                        onChange={handleInternalNotesChange}
                        value={state.rating.internalNotes ?? ''}
                      />
                    </div>
                  );
                }

                return null;
              })}
          </RatingGrid>
          <StyledButton
            isLoading={isRatingUser}
            onClick={handleRatingSave}
            size="small"
          >
            Save
          </StyledButton>
        </RatingCategoryLayout>
      </Menu.Panel>
    </Menu>
  ) : null;
};
